import React, { useEffect, useState } from "react";
import { LangMenu } from "../i18n";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, Play, Plus, Minus, BookOpen, FileText, Award, Lock, Loader2, Video, ListMusic, ChevronDown, X } from "lucide-react";
import api, { API } from "../lib/api";
import CourseReviews from "../components/CourseReviews";
import EmailHint from "../components/EmailHint";
import { toast } from "sonner";
import { visitorId, journey } from "../lib/journey";
import { getUtm } from "../lib/utm";

// Shown right after the Blueprint ebook payment: the video course offer (3 Nov 2026).
// /blueprint/course?token=<ebook access token>

const G = "#C88A24", GB = "#E5A83A", IVORY = "#F3EFE7", MUTED = "#A79F92", INK = "#080605";
const DISPLAY = "'Anton', 'Oswald', sans-serif";
const SERIF = "'Fraunces', Georgia, serif";
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const mediaUrl = (u) => (!u ? u : u.startsWith("http") ? u : `${API.replace(/\/api$/, "")}${u}`);

const accessText = (d) => {
    if (!d || d <= 0) return "Lifetime access";
    if (d % 365 === 0) return d === 365 ? "1 year of access" : `${d / 365} years of access`;
    if (d % 30 === 0) return `${d / 30} months of access`;
    return `${d} days of access`;
};

const Cta = ({ onClick, busy, children, testid }) => (
    <button onClick={onClick} disabled={busy} data-testid={testid}
        className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl font-semibold tracking-wide text-[15px] transition-all disabled:opacity-70"
        style={{ background: `linear-gradient(135deg, ${GB}, ${G})`, color: INK, boxShadow: "0 10px 40px -10px rgba(200,138,36,.55)" }}>
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
        {children}
        {!busy && <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />}
    </button>
);

export default function CourseOffer() {
    // 3 Nov 2026 (RK): the same page also runs publicly at /video-course (anyone can buy; name/email/WhatsApp form)
    const isPublic = window.location.pathname.startsWith("/video-course");
    const token = new URLSearchParams(window.location.search).get("token") || "";
    const [form, setForm] = useState(null); // public checkout form: null = closed
    const [d, setD] = useState(null);
    const [state, setState] = useState("loading");
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [faq, setFaq] = useState(-1);
    const [tab, setTab] = useState(0);
    const [deck, setDeck] = useState(0);
    const reduce = useReducedMotion();
    const STILLS = ["/course/intro.webp", "/course/lesson.webp", "/course/keypoint.webp", "/course/steps.webp"];
    const thumb = (t, i) => t.thumbnail || STILLS[i % STILLS.length];
    const fallbackImg = (i) => (e) => { const f = STILLS[i % STILLS.length]; if (!e.currentTarget.src.endsWith(f)) e.currentTarget.src = f; };
    useEffect(() => {  // the one ambient motion on the page: the lesson deck in the hero
        if (reduce || state !== "ok") return undefined;
        const id = setInterval(() => setDeck((x) => (x + 1) % 8), 3200);
        return () => clearInterval(id);
    }, [reduce, state]);
    const ebookUrl = `/blueprint/read?token=${encodeURIComponent(token)}`;

    useEffect(() => {
        if (isPublic) {
            api.get("/course-page").then((r) => { setD(r.data); setState("ok"); }).catch(() => window.location.replace("/courses"));
            return;
        }
        if (!token) { window.location.replace("/blueprint"); return; }
        api.get("/blueprint/upsell", { params: { token } }).then((r) => {
            if (!r.data.show) {
                window.location.replace(r.data.enrolled ? r.data.learn_path : ebookUrl);
                return;
            }
            setD(r.data); setState("ok");
        }).catch(() => window.location.replace(ebookUrl));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const decline = async () => {
        try { await api.post("/blueprint/upsell/decline", { token }); } catch { /* ignore */ }
        window.location.href = ebookUrl;
    };

    const loadRzp = () => new Promise((res) => {
        if (window.Razorpay) return res(true);
        const s = document.createElement("script"); s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => res(true); s.onerror = () => res(false); document.body.appendChild(s);
    });

    const buy = async () => {
        if (isPublic) { setForm((f) => f || { name: "", email: "", phone: "" }); journey("checkout", "video course form"); return; }
        setBusy(true);
        try {
            const ok = await loadRzp();
            if (!ok) { toast.error("Could not load the payment window. Please try again."); setBusy(false); return; }
            const { data } = await api.post("/blueprint/upsell/order", { token });
            if (data.already) { window.location.href = data.learn_path; return; }
            const onPaid = async (r) => {
                    setState("unlocking");
                    try {
                        const v = await api.post("/blueprint/upsell/verify", { token, order_id: r.razorpay_order_id, payment_id: r.razorpay_payment_id, signature: r.razorpay_signature });
                        window.location.href = `/auth/magic?token=${encodeURIComponent(v.data.login_token)}&next=${encodeURIComponent(v.data.learn_path)}`;
                    } catch {
                        setState("ok"); setBusy(false);
                        toast.error("Payment received but we couldn't confirm it here. Check your email for the login link, or WhatsApp us.");
                    }
            };
            if (data.preview) {  // PREVIEW site only: simulated test payment, no money moves
                if (window.confirm(`PREVIEW test payment of ₹${data.amount / 100}. No real money. Continue?`)) await onPaid({ razorpay_order_id: data.order_id, razorpay_payment_id: "pay_preview", razorpay_signature: "preview" });
                else setBusy(false);
                return;
            }
            const rzp = new window.Razorpay({
                key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
                name: "Blueprint Video Course", description: "By Ravi Kaushal · MuviArts",
                prefill: { name: data.name, email: data.email, contact: data.contact }, theme: { color: G },
                handler: onPaid,
                modal: { ondismiss: () => setBusy(false) },
            });
            rzp.open();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
            setBusy(false);
        }
    };

    const payPublic = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const ok = await loadRzp();
            if (!ok) { toast.error("Could not load the payment window. Please try again."); setBusy(false); return; }
            const { data } = await api.post("/course-page/order", { ...form, visitor_id: visitorId(), utm: (() => { try { return getUtm(); } catch { return {}; } })() });
            if (data.already) {
                toast.success("You already have this course. Log in with this email to watch it.");
                window.location.href = data.login_path || "/login"; return;
            }
            const onPaid = async (r) => {
                setForm(null); setState("unlocking");
                try {
                    const v = await api.post("/course-page/verify", { order_id: r.razorpay_order_id, payment_id: r.razorpay_payment_id, signature: r.razorpay_signature });
                    window.location.href = `/auth/magic?token=${encodeURIComponent(v.data.login_token)}&next=${encodeURIComponent(v.data.learn_path)}`;
                } catch {
                    setState("ok"); setBusy(false);
                    toast.error("Payment received but we couldn't confirm it here. Check your email for the login link, or WhatsApp us.");
                }
            };
            if (data.preview) {
                if (window.confirm(`PREVIEW test payment of ₹${data.amount / 100}. No real money. Continue?`)) await onPaid({ razorpay_order_id: data.order_id, razorpay_payment_id: "pay_preview", razorpay_signature: "preview" });
                else setBusy(false);
                return;
            }
            const rzp = new window.Razorpay({
                key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
                name: "Blueprint Video Course", description: "By Ravi Kaushal · MuviArts",
                prefill: { name: data.name, email: data.email, contact: data.contact }, theme: { color: G },
                handler: onPaid, modal: { ondismiss: () => setBusy(false) },
            });
            rzp.open();
        } catch (err) {
            toast.error(err.response?.data?.detail ? (typeof err.response.data.detail === "string" ? err.response.data.detail : "Please check your details.") : "Something went wrong. Please try again.");
            setBusy(false);
        }
    };

    if (state === "loading" || state === "unlocking") {
        return (
            <div className="min-h-screen grid place-items-center" style={{ background: INK, color: IVORY }} data-testid="course-offer-loading">
                <div className="text-center px-6">
                    <Loader2 className="h-9 w-9 animate-spin mx-auto" style={{ color: G }} />
                    {state === "unlocking" && <p className="mt-5 text-lg" style={{ fontFamily: SERIF }}>Unlocking your course…</p>}
                </div>
            </div>
        );
    }

    const c = d.course;
    const access = accessText(c.validity_days);
    const tracks = [];
    c.modules.forEach((m, mi) => (m.items || m.titles.map((t) => ({ title: t }))).forEach((it) => tracks.push({ ...it, module: mi })));
    const FAQ = [
        ["How do I watch the lessons?", `Right after payment you're logged in to your MuviArts account and lesson 1 opens. You also get an email with a login link. Later, log in at muviarts.in/login with ${d.email || "the same email"} and open My courses.`],
        ["Is this the same as the ebook?", "No. The ebook is the map. In the course RK teaches each part on camera, with a one-page Action Sheet for every lesson, practice links and 5 bonus guides on release, royalties and promotion."],
        ["What language is it in?", "RK teaches in Hinglish, the way he talks to artists every day. The Action Sheets and guides are in English."],
        ["How long can I watch it?", `${access}, including every update and new lesson RK adds in that time. Watch on your phone or laptop as many times as you like.`],
        isPublic ? ["Do I need the ebook first?", "No. The course stands on its own. If you also want the written version, the Blueprint ebook is on muviarts.in/blueprint."]
            : ["Can I buy it later?", "This price is for Blueprint readers on this page. Once you leave, it may not be available at the same price."],
    ];
    return (
        <div className="min-h-screen co" style={{ background: INK, color: IVORY }} data-testid="course-offer-page">
            <style>{CSS}</style>
            {isPublic && (
                <div style={{ borderBottom: "1px solid rgba(255,255,255,.07)" }}>
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between text-[13px]">
                        <a href="/" className="font-semibold tracking-[0.2em] text-xs" style={{ color: IVORY }}>MUVIARTS</a>
                        <span className="flex items-center gap-3"><a href="/login" className="underline underline-offset-4 co-link" style={{ color: MUTED }}>Already a student? Log in</a><LangMenu dark /></span>
                    </div>
                </div>
            )}
            {/* payment confirmed (after-ebook offer only) */}
            {!isPublic && <div style={{ background: "rgba(200,138,36,.08)", borderBottom: "1px solid rgba(200,138,36,.22)" }}>
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-1.5 sm:gap-3 text-[13px] text-center sm:text-left">
                    <span className="flex items-center justify-center gap-2"><Check className="h-4 w-4 shrink-0" style={{ color: GB }} />Payment received{d.name ? `, ${d.name}` : ""}. {d.kit ? "Your ebook and Release Kit are ready." : "Your ebook is ready."}</span>
                    {/* 4 Nov 2026 (RK): Release Kit buyers can open the kit from here too */}
                    <span className="flex flex-wrap items-center justify-center gap-2 mt-1 sm:mt-0">
                        <button onClick={decline} className="shrink-0 rounded-full px-3.5 py-1.5 text-[12px] font-semibold co-link" style={{ border: "1px solid rgba(255,255,255,.18)", color: IVORY }} data-testid="offer-skip-top">Open ebook</button>
                        {d.kit && <a href={`/blueprint/kit?token=${encodeURIComponent(token)}`} className="shrink-0 rounded-full px-3.5 py-1.5 text-[12px] font-semibold co-link" style={{ border: `1px solid ${G}`, color: GB }} data-testid="offer-open-kit">Open Release Kit</a>}
                        <LangMenu dark />
                    </span>
                </div>
                {d.email && <EmailFixLine token={token} email={d.email} onFixed={(e) => setD({ ...d, email: e })} />}
            </div>}

            {/* hero */}
            <section className="relative overflow-hidden">
                <div className="co-glow" aria-hidden />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-5 sm:pt-12 pb-10 sm:pb-16 grid lg:grid-cols-[1.05fr_1fr] gap-6 lg:gap-14 items-center">
                    <motion.div initial={reduce ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="text-center lg:text-left">
                        <p className="text-sm" style={{ color: GB }}>{d.kicker}</p>
                        <h1 className="mt-3 uppercase leading-[0.95] text-[32px] sm:text-[46px] lg:text-[58px]" style={{ fontFamily: DISPLAY }} data-testid="offer-headline">{d.headline}</h1>
                        <p className="mt-3 text-[15px] sm:text-[17px] leading-snug max-w-xl mx-auto lg:mx-0" style={{ color: "#D9D2C6" }}>{d.sub}</p>
                        <div className="mt-5 flex flex-col sm:flex-row items-center sm:items-end justify-center lg:justify-start gap-1 sm:gap-4">
                            <span className="inline-flex items-end gap-3">{isPublic && Number(d.original_price) > Number(d.price) && <span className="text-[24px] sm:text-[30px] leading-none line-through decoration-2" style={{ fontFamily: DISPLAY, color: MUTED }} data-testid="offer-original-price">{inr(d.original_price)}</span>}<span className="text-[44px] sm:text-[56px] leading-none" style={{ fontFamily: DISPLAY, color: GB }} data-testid="offer-price">{inr(d.price)}</span></span>
                            <span className="sm:pb-1.5 text-sm leading-snug" style={{ color: MUTED }}>one-time payment<br className="hidden sm:block" /><span className="sm:hidden"> </span>{isPublic ? accessText(d.course.validity_days).toLowerCase() : "for Blueprint readers"}</span>
                        </div>
                        <div className="mt-5 flex flex-col sm:flex-row justify-center lg:justify-start gap-2.5">
                            <Cta onClick={buy} busy={busy} testid="offer-buy-hero">{d.cta}</Cta>
                            <a href="#tracklist" className="co-ghost rounded-xl inline-flex items-center justify-center gap-2 px-6 py-4 text-[15px] font-semibold" data-testid="offer-see-inside">
                                <ListMusic className="h-5 w-5" /> See all 22 lessons</a>
                        </div>
                        <p className="mt-3 text-xs flex items-center justify-center lg:justify-start gap-2" style={{ color: MUTED }}><Lock className="h-3.5 w-3.5" /> Secure payment by Razorpay: UPI, cards, net banking</p>
                        <div className="lg:hidden mt-6 grid grid-cols-4 gap-2 text-center">
                            {[[c.modules.length, "modules"], [c.lessons, "lessons"], [c.lessons, "Action Sheets"], [5, "bonus guides"]].map(([n, l]) => (
                                <div key={l} className="py-2.5 rounded-xl" style={{ border: "1px solid rgba(255,255,255,.08)" }}>
                                    <p className="text-xl" style={{ fontFamily: DISPLAY, color: IVORY }}>{n}</p><p className="text-[10px] leading-tight" style={{ color: MUTED }}>{l}</p>
                                </div>
                            ))}
                        </div>
                    </motion.div>

                    {/* now playing deck */}
                    <motion.div initial={reduce ? false : { opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }} className="order-first lg:order-none">
                        <div className="co-deck" data-testid="offer-deck">
                            {tracks.slice(0, 8).map((t, i) => (
                                <img key={i} src={thumb(t, i)} onError={fallbackImg(i)} alt="" className={`co-deck-img ${i === deck ? "on" : i === (deck + 7) % 8 ? "was" : ""}`} />
                            ))}
                            {d.preview && (
                                <button onClick={() => setPlaying(true)} className="co-play absolute right-3 bottom-3" data-testid="offer-preview-play" aria-label="Watch lesson 1 free">
                                    <Play className="h-5 w-5" fill="currentColor" /> <span>Watch lesson 1 free</span>
                                </button>
                            )}
                        </div>
                        <div className="mt-2.5 flex items-center justify-center lg:justify-start gap-3 text-[13px] max-w-full" style={{ color: MUTED }}>
                            <span className="co-eq" aria-hidden><i /><i /><i /><i /></span>
                            <span className="truncate">Lesson {deck + 1} of {c.lessons}: <span style={{ color: IVORY }}>{tracks[deck]?.title}</span></span>
                        </div>
                        <div className="hidden lg:grid mt-4 grid-cols-4 gap-2 text-center">
                            {[[c.modules.length, "modules"], [c.lessons, "lessons"], [c.lessons, "Action Sheets"], [5, "bonus guides"]].map(([n, l]) => (
                                <div key={l} className="py-3 rounded-xl" style={{ border: "1px solid rgba(255,255,255,.08)" }}>
                                    <p className="text-2xl" style={{ fontFamily: DISPLAY, color: IVORY }}>{n}</p><p className="text-[11px]" style={{ color: MUTED }}>{l}</p>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* tracklist */}
            <section id="tracklist" style={{ background: "#0F0B08" }}>
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
                    <h2 className="uppercase text-[30px] sm:text-4xl lg:text-[44px] leading-[0.95] text-center sm:text-left" style={{ fontFamily: DISPLAY }}>The tracklist</h2>
                    <p className="mt-2 text-[15px] leading-snug max-w-xl mx-auto sm:mx-0 text-center sm:text-left" style={{ color: MUTED }}>{c.duration ? `${c.duration} ` : ""}in {c.lessons} short lessons, in the order RK teaches them. Tap a module to see its lessons.</p>
                    <div className="mt-6" data-testid="offer-curriculum">
                        {c.modules.map((m, i) => {
                            const startN = c.modules.slice(0, i).reduce((a, x) => a + x.lessons, 0);
                            const isOpen = open === i;
                            return (
                                <div key={i} className="co-mod" data-open={isOpen}>
                                    <button className="w-full flex items-center gap-4 py-3.5 text-left" onClick={() => setOpen(isOpen ? -1 : i)} aria-expanded={isOpen}>
                                        <span className="co-modn">{i + 1}</span>
                                        <span className="flex-1 min-w-0">
                                            <span className="block text-[17px] sm:text-lg font-semibold">{m.title.replace(/^Module \d+ · /, "")}</span>
                                            <span className="block text-xs mt-0.5" style={{ color: MUTED }}>{m.lessons} lessons</span>
                                        </span>
                                        <ChevronDown className="h-5 w-5 shrink-0 transition-transform" style={{ color: G, transform: isOpen ? "rotate(180deg)" : "none" }} />
                                    </button>
                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.ul initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: reduce ? 0 : 0.3 }} className="overflow-hidden">
                                                {(m.items || m.titles.map((t) => ({ title: t }))).map((t, j) => {
                                                    const n = startN + j;
                                                    return (
                                                        <li key={j} className="co-track">
                                                            <span className="co-trackn">{String(n + 1).padStart(2, "0")}</span>
                                                            <img src={thumb(t, n)} onError={fallbackImg(n)} alt="" loading="lazy" className="co-trackimg" />
                                                            <span className="flex-1 min-w-0">
                                                                <span className="block text-[15px] font-medium leading-snug">{t.title}</span>
                                                                {t.duration && <span className="text-xs" style={{ color: MUTED }}>{t.duration}</span>}
                                                            </span>
                                                            {n === 0 && d.preview && <button onClick={() => setPlaying(true)} className="co-free">Watch free</button>}
                                                        </li>
                                                    );
                                                })}
                                            </motion.ul>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* what you get: interactive */}
            <section>
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16 grid lg:grid-cols-2 gap-6 lg:gap-14 items-center">
                    <div className="text-center lg:text-left">
                        <h2 className="uppercase text-[30px] sm:text-4xl lg:text-[44px] leading-[0.95]" style={{ fontFamily: DISPLAY }}>More than videos</h2>
                        <div className="mt-5 flex flex-wrap justify-center lg:justify-start gap-2" role="tablist">
                            {GET.map((g, i) => (
                                <button key={g.k} role="tab" aria-selected={tab === i} onClick={() => setTab(i)} className={`co-tab ${tab === i ? "on" : ""}`} data-testid={`offer-tab-${g.k}`}>
                                    <g.icon className="h-4 w-4" /> {g.label}
                                </button>
                            ))}
                        </div>
                        <AnimatePresence mode="wait">
                            <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: reduce ? 0 : 0.25 }} className="mt-4">
                                <p className="text-lg sm:text-xl leading-snug" style={{ fontFamily: SERIF }}>{GET[tab].title(c, access)}</p>
                                <ul className="mt-3 space-y-2 max-w-md mx-auto lg:mx-0">
                                    {GET[tab].points.map((p) => <li key={p} className="lg:flex lg:gap-3 text-[15px] leading-snug" style={{ color: "#D9D2C6" }}><Check className="inline lg:block h-4 w-4 lg:h-5 lg:w-5 mr-1.5 lg:mr-0 -mt-0.5 lg:mt-0 shrink-0" style={{ color: GB }} />{p}</li>)}
                                </ul>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                    <div className="relative aspect-[4/3] w-full max-w-md lg:max-w-none mx-auto">
                        <AnimatePresence mode="wait">
                            <motion.img key={tab} src={GET[tab].img} alt="" initial={{ opacity: 0, rotate: -2, y: 12 }} animate={{ opacity: 1, rotate: GET[tab].rot, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: reduce ? 0 : 0.35 }}
                                className={`absolute inset-0 m-auto shadow-2xl rounded-2xl ${GET[tab].cls}`} style={{ border: "1px solid rgba(200,138,36,.35)" }} />
                        </AnimatePresence>
                    </div>
                </div>
            </section>

            {/* what you'll learn */}
            <section style={{ background: "#0F0B08" }}>
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
                    <h2 className="uppercase text-[30px] sm:text-4xl lg:text-[44px] leading-[0.95] max-w-3xl mx-auto sm:mx-0 text-center sm:text-left" style={{ fontFamily: DISPLAY }}>By the last lesson you can</h2>
                    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-px rounded-2xl overflow-hidden" style={{ background: "rgba(255,255,255,.08)" }}>
                        {d.bullets.map((b, i) => (
                            <div key={i} className="px-4 py-3.5 sm:p-5 text-center sm:text-left" style={{ background: "#0F0B08" }}>
                                <p className="text-[15px] leading-snug"><Check className="inline h-4 w-4 mr-1.5 -mt-0.5" style={{ color: GB }} />{b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* RK */}
            <section>
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16 grid md:grid-cols-[0.9fr_1.1fr] gap-6 md:gap-10 items-center">
                    <img src="/course/rk.jpg" alt="Ravi Kaushal" loading="lazy" className="w-full max-w-[280px] md:max-w-sm mx-auto aspect-[4/5] object-cover object-top rounded-2xl" style={{ border: "1px solid rgba(200,138,36,.3)" }} />
                    <div className="text-center md:text-left">
                        <h2 className="uppercase text-[30px] sm:text-4xl lg:text-[44px] leading-[0.95]" style={{ fontFamily: DISPLAY }}>Ravi Kaushal (RK)</h2>
                        <p className="mt-3 text-[15px] sm:text-[17px] leading-snug" style={{ color: "#D9D2C6" }}>
                            Singer, songwriter, composer and rapper, and founder of MuviArts, the production and design company that makes
                            music, videos and campaigns for independent artists. This course is the process he uses himself, from the
                            first riyaz to the release and the money after it.</p>
                        <p className="mt-4 text-lg sm:text-xl" style={{ fontFamily: SERIF, color: GB, fontStyle: "italic" }}>"One lesson, one action, every day."</p>
                    </div>
                </div>
            </section>

            {/* student reviews (only when there are some) */}
            <div className="max-w-6xl mx-auto px-4 sm:px-6"><CourseReviews slug={c.slug} dark className="py-10 sm:py-14" showEmpty={isPublic} /></div>

            {/* FAQ */}
            <section style={{ background: "#0F0B08" }}>
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
                    <h2 className="uppercase text-[30px] sm:text-4xl lg:text-[44px] leading-[0.95] text-center sm:text-left" style={{ fontFamily: DISPLAY }}>Questions</h2>
                    <div className="mt-5">
                        {FAQ.map(([q, a], i) => (
                            <div key={i} style={{ borderBottom: "1px solid rgba(255,255,255,.1)" }}>
                                <button className="w-full flex items-center justify-between gap-4 py-3.5 text-left font-semibold text-[15px]" onClick={() => setFaq(faq === i ? -1 : i)} aria-expanded={faq === i}>
                                    {q} {faq === i ? <Minus className="h-4 w-4 shrink-0" style={{ color: G }} /> : <Plus className="h-4 w-4 shrink-0" style={{ color: G }} />}
                                </button>
                                {faq === i && <p className="pb-4 text-[14.5px] leading-snug" style={{ color: "#CFC8BC" }}>{a}</p>}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* final CTA */}
            <section className="pb-28 sm:pb-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16">
                    <div className="co-final text-center px-5 py-9 sm:py-14">
                        <h2 className="uppercase text-[30px] sm:text-5xl leading-[0.95]" style={{ fontFamily: DISPLAY }}>{isPublic ? "Start the video course" : "Add the video course"}</h2>
                        <p className="mt-3 text-[15px] leading-snug" style={{ color: "#D9D2C6" }}>{c.lessons} lessons, {c.lessons} Action Sheets, 5 bonus guides and a certificate. {access}, including every update.</p>
                        <p className="mt-4 text-5xl" style={{ fontFamily: DISPLAY, color: GB }}>{isPublic && Number(d.original_price) > Number(d.price) && <span className="text-2xl line-through mr-3 align-middle" style={{ color: MUTED }}>{inr(d.original_price)}</span>}{inr(d.price)}</p>
                        <div className="mt-5 flex justify-center"><Cta onClick={buy} busy={busy} testid="offer-buy-bottom">{d.cta}</Cta></div>
                        {!isPublic && <button onClick={decline} className="mt-4 text-sm underline underline-offset-4 co-link" style={{ color: MUTED }} data-testid="offer-decline">{d.decline}</button>}
                    </div>
                </div>
            </section>

            {/* public checkout form */}
            <AnimatePresence>
                {form && (
                    <motion.div className="fixed inset-0 z-[90] grid place-items-center px-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ background: "rgba(0,0,0,.72)" }} onClick={() => !busy && setForm(null)} data-testid="course-buy-form">
                        <motion.form onSubmit={payPublic} onClick={(e) => e.stopPropagation()} initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
                            className="w-full max-w-md rounded-2xl p-6 sm:p-7 text-center" style={{ background: "#120d08", border: "1px solid rgba(200,138,36,.35)" }}>
                            <div className="flex justify-end -mt-2 -mr-2"><button type="button" onClick={() => setForm(null)} aria-label="Close" className="p-1" style={{ color: MUTED }}><X className="h-5 w-5" /></button></div>
                            <h3 className="uppercase text-[28px] leading-none" style={{ fontFamily: DISPLAY }}>Where should we send your access?</h3>
                            <p className="mt-2 text-sm" style={{ color: MUTED }}>Your login link and receipt go to this email. We use WhatsApp only for course updates and support.</p>
                            {[["name", "Your name", "text", "name"], ["email", "Email", "email", "email"], ["phone", "WhatsApp number", "tel", "tel"]].map(([k, ph, type, ac]) => (
                                <input key={k} required type={type} autoComplete={ac} placeholder={ph} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                    className="mt-3 w-full rounded-xl px-4 py-3.5 text-[15px] outline-none text-center" style={{ background: "#0b0806", border: "1px solid rgba(255,255,255,.14)", color: IVORY }} data-testid={`course-buy-${k}`} />
                            ))}
                            <EmailHint dark className="mt-2 justify-center" email={form.email} onUse={(fixed) => setForm({ ...form, email: fixed })} />
                            <button type="submit" disabled={busy} className="mt-5 w-full py-4 rounded-xl font-semibold text-[15px] inline-flex items-center justify-center gap-2 disabled:opacity-70" style={{ background: `linear-gradient(135deg, ${GB}, ${G})`, color: INK }} data-testid="course-buy-pay">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Pay {inr(d.price)} securely
                            </button>
                            <p className="mt-3 text-[11px]" style={{ color: MUTED }}>Secure payment by Razorpay · UPI, cards, net banking</p>
                        </motion.form>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* sticky bar on phones */}
            <div className="fixed bottom-0 inset-x-0 sm:hidden z-40 px-4 py-3 flex items-center gap-3" style={{ background: "rgba(9,7,6,.96)", borderTop: "1px solid rgba(200,138,36,.35)" }}>
                <div className="leading-tight"><p className="text-xl" style={{ fontFamily: DISPLAY, color: GB }}>{inr(d.price)}</p><p className="text-[11px]" style={{ color: MUTED }}>video course</p></div>
                <button onClick={buy} disabled={busy} className="flex-1 py-3.5 rounded-xl font-semibold text-[15px] inline-flex items-center justify-center gap-2" style={{ background: `linear-gradient(135deg, ${GB}, ${G})`, color: INK }} data-testid="offer-buy-sticky">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {isPublic ? "Get the course" : "Add the course"}
                </button>
            </div>

            {/* free lesson player */}
            <AnimatePresence>
                {playing && d.preview && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(0,0,0,.85)" }} onClick={() => setPlaying(false)} data-testid="offer-player">
                        <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between mb-3"><p className="text-sm" style={{ color: GB }}>Free lesson: {d.preview.title}</p>
                                <button onClick={() => setPlaying(false)} className="p-2" aria-label="Close"><X className="h-5 w-5" /></button></div>
                            <video src={mediaUrl(d.preview.video)} controls autoPlay playsInline controlsList="nodownload" className="w-full aspect-video bg-black" data-testid="offer-preview-video" />
                            <div className="mt-4 flex justify-center"><Cta onClick={() => { setPlaying(false); buy(); }} busy={busy} testid="offer-buy-player">{d.cta}</Cta></div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

const GET = [
    { k: "videos", label: "Video lessons", icon: Video, img: "/course/get-videos.webp", rot: 0, cls: "w-full h-full object-cover",
      title: (c) => `${c.lessons} lessons with RK, ${c.duration || "short and focused"}`,
      points: ["Key points appear on screen, so you can take notes as you watch", "Watch on your phone or laptop, pick up where you left off", "Mark lessons done and track your progress"] },
    { k: "sheets", label: "Action Sheets", icon: FileText, img: "/course/get-sheets.webp", rot: 0, cls: "w-full h-full object-cover",
      title: () => "A one-page Action Sheet for every lesson",
      points: ["The lesson in short, so you can revise in a minute", "Steps to do today, with a reflection prompt", "RK's practice links: riyaz, tanpura, alankars, recording, song process"] },
    { k: "guides", label: "Bonus guides", icon: BookOpen, img: "/course/get-guides.webp", rot: 0, cls: "w-full h-full object-cover",
      title: () => "5 bonus guides for the business side",
      points: ["Artist identity workbook", "Release checklist and metadata, choosing a distributor", "Royalties in India and your first 30 days after release"] },
    { k: "cert", label: "Certificate", icon: Award, img: "/course/get-cert.webp", rot: 0, cls: "w-full h-full object-cover",
      title: (c, access) => `Finish all lessons and download your certificate. ${access}.`,
      points: ["A MuviArts certificate with your name", "Share it on Instagram and LinkedIn", "Keep watching any lesson again"] },
];

const CSS = `
.co ::selection{background:${G};color:${INK}}
.co a:focus-visible,.co button:focus-visible{outline:2px solid ${GB};outline-offset:3px}
.co-glow{position:absolute;inset:-20% -10% auto auto;width:70vw;height:70vw;max-width:900px;max-height:900px;background:radial-gradient(circle at center,rgba(200,138,36,.18),transparent 60%);pointer-events:none}
.co-ghost{border:1px solid rgba(229,168,58,.5);color:${IVORY};transition:background .2s,border-color .2s}
.co-ghost:hover{background:rgba(229,168,58,.08);border-color:${GB}}
.co-link:hover{color:${IVORY}!important}
.co-deck{position:relative;aspect-ratio:16/9;overflow:hidden;border:1px solid rgba(200,138,36,.45);box-shadow:0 40px 90px -40px rgba(0,0,0,.9),0 0 0 6px rgba(200,138,36,.06)}
.co-deck-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;transform:translateX(100%);transition:transform .75s cubic-bezier(.22,1,.36,1);z-index:0}
.co-deck-img.on{transform:none;z-index:2}
.co-deck-img.was{transform:translateX(-25%);z-index:1}
 .co-play{z-index:3}
.co-play{display:inline-flex;align-items:center;gap:.5rem;padding:.7rem 1rem;background:${GB};color:${INK};font-weight:700;font-size:13px;transition:transform .15s}
.co-play:hover{transform:translateY(-1px)}
@media (max-width:420px){.co-play span{display:none}}
.co-eq{display:inline-flex;align-items:flex-end;gap:2px;height:12px}
.co-eq i{width:3px;background:${GB};animation:coeq 1s ease-in-out infinite}
.co-eq i:nth-child(2){animation-delay:.2s}.co-eq i:nth-child(3){animation-delay:.4s}.co-eq i:nth-child(4){animation-delay:.1s}
@keyframes coeq{0%,100%{height:3px}50%{height:12px}}
.co-mod{border-top:1px solid rgba(255,255,255,.1)}
.co-mod:last-child{border-bottom:1px solid rgba(255,255,255,.1)}
.co-modn{width:2.5rem;height:2.5rem;display:grid;place-items:center;font-family:${DISPLAY};font-size:1.25rem;color:${GB};border:1px solid rgba(200,138,36,.45);flex-shrink:0}
.co-mod[data-open=true] .co-modn{background:${G};color:${INK};border-color:${G}}
.co-track{display:flex;align-items:center;gap:.9rem;padding:.65rem 0 .65rem .25rem}
.co-trackn{width:1.75rem;font-variant-numeric:tabular-nums;font-size:13px;color:${MUTED};flex-shrink:0;text-align:right}
.co-trackimg{width:96px;aspect-ratio:16/9;object-fit:cover;border:1px solid rgba(255,255,255,.1);flex-shrink:0}
.co-free{font-size:12px;font-weight:700;padding:.4rem .7rem;border:1px solid ${GB};color:${GB}}
.co-free:hover{background:${GB};color:${INK}}
.co-tab{display:inline-flex;align-items:center;gap:.45rem;padding:.6rem 1rem;font-size:14px;font-weight:600;border:1px solid rgba(255,255,255,.14);color:#D9D2C6;transition:all .2s}
.co-tab:hover{border-color:${GB};color:${IVORY}}
.co-tab.on{background:${IVORY};color:${INK};border-color:${IVORY}}
.co-final{border:1px solid rgba(200,138,36,.45);background:radial-gradient(ellipse at top,rgba(200,138,36,.16),transparent 65%)}
.co-play{border-radius:999px}.co-tab{border-radius:999px}.co-free{border-radius:999px}.co-modn{border-radius:10px}
.co-deck{border-radius:18px}.co-trackimg{border-radius:8px}.co-final{border-radius:24px}
@media (prefers-reduced-motion:reduce){.co-eq i{animation:none;height:8px}.co-deck-img,.co-deck-img.was{transition:none}}
`;


// 4 Nov 2026 (RK): buyers who mistyped their email can fix it here and get the ebook email again.
function EmailFixLine({ token, email, onFixed }) {
    const [open, setOpen] = useState(false);
    const [val, setVal] = useState(email);
    const [busy, setBusy] = useState(false);
    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const r = await api.post("/blueprint/fix-email", { token, email: val.trim() });
            onFixed(r.data.email); setOpen(false);
            toast.success(`Sent again to ${r.data.email}. Check your inbox and spam folder.`);
        } catch (err) { const dt = err.response?.data?.detail; toast.error(typeof dt === "string" ? dt : "Please enter a valid email."); }
        finally { setBusy(false); }
    };
    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-3 -mt-1 text-[12px] text-center sm:text-left" style={{ color: "#B7B0A5" }} data-testid="offer-email-line">
            {!open ? (
                <span>Ebook emailed to <b style={{ color: "#F3EFE7" }} data-notranslate>{email}</b>. <button type="button" onClick={() => { setVal(email); setOpen(true); }} className="underline underline-offset-2" data-testid="offer-email-fix">Wrong email? Fix it</button></span>
            ) : (
                <form onSubmit={save} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center max-w-xl mx-auto sm:mx-0">
                    <input type="email" required value={val} onChange={(e) => setVal(e.target.value)} className="flex-1 rounded-lg px-3 py-2 text-[14px] outline-none" style={{ background: "#0b0806", border: "1px solid rgba(255,255,255,.2)", color: "#F3EFE7" }} data-testid="offer-email-input" />
                    <button disabled={busy} className="rounded-lg px-4 py-2 font-semibold disabled:opacity-60" style={{ background: "#E5A83A", color: "#080605" }} data-testid="offer-email-save">{busy ? "Sending…" : "Save & resend"}</button>
                    <EmailHint dark email={val} onUse={setVal} className="sm:basis-full" />
                </form>
            )}
        </div>
    );
}
